import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, User, CheckCircle, Broom, Phone } from "@phosphor-icons/react";
import { BUSINESS } from "@/data/site";

export default function HomePreservationGuide() {
  return (
    <article data-testid="guide-home-preservation" className="ji-container py-16 md:py-24 max-w-4xl">
      <nav className="text-sm mb-8">
        <Link to="/" className="text-[var(--ji-muted)] hover:text-[var(--ji-accent)]">Home</Link>
        <span className="mx-2 text-[var(--ji-muted)]">·</span>
        <Link to="/services" className="text-[var(--ji-muted)] hover:text-[var(--ji-accent)]">Services</Link>
        <span className="mx-2 text-[var(--ji-muted)]">·</span>
        <span className="text-[var(--ji-text)]">Home Preservation &amp; Asset Protection</span>
      </nav>

      <div className="ji-overline mb-4 inline-flex items-center gap-2">
        <Broom size={14} weight="bold" /> Service Guide · Property and Home Services
      </div>
      <h1
        style={{ fontFamily: "Cabinet Grotesk", lineHeight: 1.0 }}
        className="text-4xl md:text-6xl font-bold tracking-tight"
      >
        Home Preservation &amp;<br />
        <span className="text-[var(--ji-accent)] italic">Asset Protection.</span>
      </h1>

      <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-[var(--ji-muted)] pb-6 border-b border-[var(--ji-border)]">
        <span className="inline-flex items-center gap-1.5"><User size={14} weight="bold" /> David Joseph · Joseph Industries</span>
        <span className="inline-flex items-center gap-1.5"><Clock size={14} weight="bold" /> 4 min read</span>
        <span>Southern Louisiana · residential &amp; commercial</span>
      </div>

      <div className="prose prose-lg max-w-none mt-8 space-y-6 text-[var(--ji-text)] leading-relaxed text-base md:text-lg">
        <p>
          Your home is almost certainly the largest single asset you&rsquo;ll ever own. The furniture inside it, the
          appliances hard-wired to its systems, and the finishes on the floors and walls represent tens of thousands of
          dollars of secondary value that most owners never think about until something is damaged. Home preservation is
          the everyday work of keeping those assets in the condition they were bought in &mdash; through renovations,
          moves, life changes, and the humid Louisiana climate that ages materials faster than the rest of the country.
          Below is how we approach that work, from post-construction cleanup to the muscle it takes to move a safe.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Post-construction cleanup is preservation, not just tidying
        </h2>
        <p>
          Drywall dust and joint-compound residue are the two most underestimated threats to a newly renovated home.
          Fine particulate finds its way into refrigerator condenser coils, HVAC intakes, and appliance motors within
          days &mdash; and once inside, it insulates heat-transfer surfaces and shortens equipment life by measurable
          years. A proper post-construction cleanup is a four-pass process: heavy debris removal, HEPA vacuuming of all
          horizontal surfaces including tops of trim and cabinets, wet-wipe of every finished surface, and a final
          detail pass on glass, hardware, and fixtures. Skipping any pass is what turns a new kitchen into a warranty
          claim eighteen months later.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Moving furniture: pads, straps, and floor protection
        </h2>
        <p>
          Every furniture move &mdash; whether it&rsquo;s a room-to-room reshuffle or a short-distance local move
          &mdash; carries the same risks: gouged hardwood floors, torn carpet, dinged door frames, and cracked leg
          joints on antique pieces. Our standard protocol is moving pads on every wooden or upholstered piece,
          plastic-wrap on any surface that could catch a splinter, floor runners in every hallway the crew crosses more
          than twice, and corner guards on doorway trim in older homes with plaster that chips easily. The 30 minutes
          this setup takes prevents damage claims that routinely run into thousands. For heavier or awkward items
          &mdash; safes, pianos, hot tubs &mdash; we use dollies rated well above the piece&rsquo;s weight, with two
          crew minimum and a spotter on stairs.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Junk removal &amp; estate cleanouts, done respectfully
        </h2>
        <p>
          Cleaning out a rental after a bad tenant, a garage that&rsquo;s accumulated 25 years, or a family home after
          a loss is emotional and logistical work at once. We approach every cleanout with two rules: nothing gets
          removed without an owner&rsquo;s sign-off on the pile, and anything with sentimental value gets set aside
          rather than hauled. We recycle metals, donate usable furniture and appliances to local nonprofits when the
          owner asks, and haul only what remains to the landfill. For property managers and investors, we handle
          post-eviction cleanouts on 24-hour notice with photo documentation for insurance and rental-turnover records.
        </p>

        <h2 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight mt-10">
          Furniture &amp; fitness assembly for long-term durability
        </h2>
        <p>
          A poorly assembled bed frame is a lawsuit waiting to happen; a treadmill with the wrong bolt torque will fail
          under the first serious workout. We assemble to manufacturer spec, torque bolts to the values called for in
          the instructions (not &ldquo;until it feels tight&rdquo;), and test every moving joint before leaving.
          IKEA-style flat-pack, Peloton bikes, full home gyms, cribs, playsets, patio furniture &mdash; each has its own
          quirks, and we&rsquo;ve seen most of them. The box is broken down, taken with us, and disposed of properly so
          your driveway doesn&rsquo;t become a cardboard graveyard. Asset protection isn&rsquo;t glamorous, but it
          compounds silently over the years &mdash; and it&rsquo;s the difference between a home that ages gracefully
          and one that needs constant rescue work.
        </p>
      </div>

      <div className="mt-16 ji-card p-8 md:p-10" style={{ borderRadius: 4, background: "var(--ji-primary)" }}>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.22em] text-[var(--ji-accent)] mb-2">Free quote · Louisiana licensed &amp; insured</div>
            <h3 style={{ fontFamily: "Cabinet Grotesk" }} className="text-2xl md:text-3xl font-bold tracking-tight text-white">
              Have a home or property project that needs a professional crew?
            </h3>
            <p className="mt-3 text-white/80">
              Post-construction cleanup, junk removal, assembly, in-home moves, heavy lifting &mdash; one call, one crew,
              one invoice. Free estimates on every job.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link
              to="/contact"
              data-testid="guide-cta-quote"
              className="ji-btn-accent inline-flex items-center justify-center gap-2 h-12 px-5 text-sm font-semibold"
              style={{ borderRadius: 4 }}
            >
              Get a free quote <ArrowRight size={14} weight="bold" />
            </Link>
            <a
              href={`tel:${BUSINESS.primaryPhone.tel}`}
              data-testid="guide-cta-call"
              className="ji-btn-outline inline-flex items-center justify-center gap-2 h-12 px-5 text-sm font-semibold bg-white/10 text-white border-white/30 hover:bg-white/20"
              style={{ borderRadius: 4 }}
            >
              <Phone size={14} weight="bold" /> {BUSINESS.primaryPhone.display}
            </a>
          </div>
        </div>
      </div>

      <div className="mt-10 text-sm text-[var(--ji-muted)] flex flex-wrap gap-4">
        <span className="ji-overline">More guides:</span>
        <Link to="/guides/appliance-installation" className="hover:text-[var(--ji-accent)] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Appliance Installation Best Practices</Link>
        <Link to="/guides/courier-logistics" className="hover:text-[var(--ji-accent)] inline-flex items-center gap-1"><CheckCircle size={12} weight="fill" /> Courier &amp; Last-Mile Logistics</Link>
      </div>
    </article>
  );
}
